import {
  MFG_SDS_SECTION_KEYS,
  MFG_SDS_SECTION_LABELS,
  type MfgSdsSectionAudit,
  type MfgSdsSectionKey,
} from "@/lib/manufacturing/msdsSections";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type {
  MfgMaterialSdsDocument,
  MsdsGenerateHints,
} from "@/lib/manufacturing/msdsDocumentTypes";
import {
  ghostBtnClass,
  inputClass,
  mfgModalShellClass,
  mfgPrimaryBtnClass,
  mfgPrimaryBtnLgClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingMaterialSdsAckModal } from "./ManufacturingMaterialSdsAckModal";
import { ManufacturingMaterialSdsBanner } from "./ManufacturingMaterialSdsBanner";
import { ManufacturingMaterialSdsGenerateForm } from "./ManufacturingMaterialSdsGenerateForm";
import { ManufacturingMaterialSdsPublishGate } from "./ManufacturingMaterialSdsPublishGate";
import { ManufacturingMaterialSdsSupplierUpload } from "./ManufacturingMaterialSdsSupplierUpload";

type Props = {
  open: boolean;
  material: MfgMaterial;
  loading: boolean;
  saving: boolean;
  generating: boolean;
  exporting: boolean;
  acknowledging: boolean;
  publishing: boolean;
  uploading: boolean;
  error: string | null;
  dirty: boolean;
  ackOpen: boolean;
  signOffChecked: boolean;
  sdsDocument: MfgMaterialSdsDocument | null;
  publishedDocument: MfgMaterialSdsDocument | null;
  sections: Record<MfgSdsSectionKey, string>;
  sectionAudit: MfgSdsSectionAudit;
  onSetSection: (key: MfgSdsSectionKey, value: string) => void;
  onSave: () => void;
  onGenerate: (hints: MsdsGenerateHints) => void;
  onAcknowledge: () => void;
  onPublish: () => void;
  onUploadSupplier: (file: File) => void;
  onSignOffChange: (checked: boolean) => void;
  onExportPdf: () => void;
  onClose: () => void;
};

function sectionChip(audit: MfgSdsSectionAudit[MfgSdsSectionKey], body: string): string | null {
  if (audit?.status === "edited") return "edited";
  if (audit?.status === "opened") return "opened";
  if (audit?.status === "ai_draft") return "ai_draft";
  if (!body.trim()) return "empty";
  return null;
}

/** MSDS-3–5 — SDS workspace: generate, upload, publish, edit-after-publish. */
export function ManufacturingMaterialSdsPanel({
  open,
  material,
  loading,
  saving,
  generating,
  exporting,
  acknowledging,
  publishing,
  uploading,
  error,
  dirty,
  ackOpen,
  signOffChecked,
  sdsDocument,
  publishedDocument,
  sections,
  sectionAudit,
  onSetSection,
  onSave,
  onGenerate,
  onAcknowledge,
  onPublish,
  onUploadSupplier,
  onSignOffChange,
  onExportPdf,
  onClose,
}: Props) {
  if (!open) return null;

  const busy =
    loading || saving || exporting || generating || acknowledging || publishing || uploading;
  const statusLabel = sdsDocument
    ? `draft v${sdsDocument.version}`
    : publishedDocument
      ? `published v${publishedDocument.version}`
      : "new draft";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4">
      <div
        className={`flex max-h-[95vh] w-full max-w-3xl flex-col overflow-hidden sm:rounded-2xl ${mfgModalShellClass}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-zinc-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
              SDS — {material.code || "Material"}
            </h3>
            <p className="text-xs text-gray-600 dark:text-zinc-400">
              {material.name}
              {` · ${statusLabel}`}
              {sdsDocument?.source === "ai_draft" ? " · AI draft" : ""}
              {sdsDocument?.source === "supplier_upload" ? " · supplier" : ""}
              {dirty ? " · unsaved" : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={ghostBtnClass} onClick={onClose} disabled={busy}>
              Close
            </button>
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              onClick={onExportPdf}
              disabled={busy}
            >
              {exporting ? "Exporting…" : "Export PDF"}
            </button>
            <button
              type="button"
              className={mfgPrimaryBtnLgClass}
              onClick={onSave}
              disabled={busy || !dirty}
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
          </div>
        </div>

        <div className="space-y-3 overflow-y-auto px-4 py-3">
          <ManufacturingMaterialSdsBanner
            document={sdsDocument}
            publishedDocument={publishedDocument}
            sectionAudit={sectionAudit}
            dirty={dirty}
          />
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
              {error}
            </div>
          ) : null}
          {!loading ? (
            <>
              <ManufacturingMaterialSdsSupplierUpload
                uploading={uploading}
                disabled={busy && !uploading}
                hasSupplierAttachment={!!sdsDocument?.supplier_attachment_id}
                onUpload={onUploadSupplier}
              />
              <ManufacturingMaterialSdsGenerateForm
                generating={generating}
                disabled={busy && !generating}
                onGenerate={onGenerate}
              />
              <ManufacturingMaterialSdsPublishGate
                draft={sdsDocument}
                publishing={publishing}
                disabled={busy && !publishing}
                signOffChecked={signOffChecked}
                onSignOffChange={onSignOffChange}
                onPublish={onPublish}
              />
            </>
          ) : null}
          {loading ? (
            <p className="text-sm text-gray-600 dark:text-zinc-300">Loading SDS…</p>
          ) : (
            MFG_SDS_SECTION_KEYS.map((key) => {
              const audit = sectionAudit[key];
              const chip = sectionChip(audit, sections[key]);
              return (
                <label key={key} className="block text-xs text-gray-700 dark:text-zinc-300">
                  <span className="mb-1 flex flex-wrap items-baseline gap-2 font-medium">
                    <span>
                      {key}. {MFG_SDS_SECTION_LABELS[key]}
                    </span>
                    {chip ? (
                      <span className="font-normal text-sky-800 dark:text-sky-300">{chip}</span>
                    ) : null}
                  </span>
                  <textarea
                    className={`${inputClass} mt-0.5 min-h-[72px] font-normal`}
                    value={sections[key]}
                    onChange={(e) => onSetSection(key, e.target.value)}
                    disabled={busy}
                    placeholder="Plain text for this section…"
                  />
                </label>
              );
            })
          )}
        </div>
      </div>
      <ManufacturingMaterialSdsAckModal
        open={ackOpen}
        acknowledging={acknowledging}
        onAcknowledge={onAcknowledge}
      />
    </div>
  );
}
